/* Solar-only native contact measurement. No contact fields, query strings or referrer enter these events.
 * The connected Google channel owns tag loading. A queued event is not a delivery receipt.
 */
(() => {
  const storageKey = 'hok.solar.inquiry.pending.v1';
  const ttl = 30 * 60 * 1000;
  const packageKeys = { 'Solar + Storage': 'hybrid', 'Solar Array': 'solar-only', 'Different size / project': 'custom', 'Help me choose': 'undecided' };
  const read = () => {
    try { return JSON.parse(window.sessionStorage.getItem(storageKey)); } catch { return null; }
  };
  const write = value => {
    try {
      if (value) window.sessionStorage.setItem(storageKey, JSON.stringify(value));
      else window.sessionStorage.removeItem(storageKey);
      return true;
    } catch { return false; }
  };
  const valid = (pending, formId) => Boolean(pending && pending.form === formId &&
    typeof pending.id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(pending.id) &&
    Number.isFinite(pending.at) && Date.now() >= pending.at && Date.now() - pending.at <= ttl &&
    ['hybrid', 'solar-only', 'custom', 'undecided'].includes(pending.package));
  const permission = method => {
    try { return window.Shopify?.customerPrivacy?.[method]?.() === true; } catch { return false; }
  };
  const initialize = marker => {
    if (marker.dataset.solarInitialized) return;
    marker.dataset.solarInitialized = 'true';
    const form = marker.closest('form');
    if (!form || form.dataset.reviewOnly === 'true') return;
    const state = marker.dataset.solarServerStatus;
    const params = new URLSearchParams(window.location.search);
    const returnedReference = params.get('hok_solar_inquiry_ref');
    const preview = marker.dataset.solarReview !== 'false' || Boolean(window.Shopify?.designMode) ||
      window.Shopify?.theme?.role !== 'main' || params.has('preview_theme_id');
    const gaDestination = /^G-[A-Z0-9]+$/.test(marker.dataset.solarGaDestination || '') ? marker.dataset.solarGaDestination : '';
    const adsDestination = /^AW-\d+\/[A-Za-z0-9_-]+$/.test(marker.dataset.solarAdsDestination || '') ? marker.dataset.solarAdsDestination : '';
    const receipt = { version: 1, server: state, tracking: 'awaiting-submission', ga: 'not-attempted', ads: preview ? 'review-suppressed' : 'not-attempted' };
    const report = () => {
      receipt.tagAvailable = typeof window.gtag === 'function';
      receipt.privacyApiAvailable = Boolean(window.Shopify?.customerPrivacy);
      receipt.reviewContext = preview;
      receipt.themeRole = ['main', 'unpublished', 'development'].includes(window.Shopify?.theme?.role) ? window.Shopify.theme.role : 'unknown';
      marker.dataset.solarTracking = receipt.tracking;
      marker.dataset.solarGaStatus = receipt.ga;
      marker.dataset.solarAdsStatus = receipt.ads;
      marker.textContent = JSON.stringify(receipt);
    };
    if (state === 'error') {
      write(null);
      receipt.tracking = 'server-error';
    }
    report();
    if (state === 'ready' || state === 'error') {
      form.addEventListener('submit', event => {
        if (event.defaultPrevented || !form.checkValidity()) return;
        const reference = form.querySelector('[data-solar-inquiry-reference]');
        const returnTo = form.querySelector('input[name="return_to"]');
        if (reference) reference.value = '';
        if (returnTo) returnTo.value = 'back';
        let id;
        try { id = window.crypto.randomUUID(); } catch { receipt.tracking = 'reference-unavailable'; report(); return; }
        if (!reference || !returnTo) { receipt.tracking = 'native-reference-route-unavailable'; report(); return; }
        const pending = {
          id, form: form.id, at: Date.now(), review: preview,
          package: packageKeys[form.querySelector('[data-solar-package-field]')?.value] || 'undecided',
          ga: false, ads: false
        };
        if (!write(pending)) { receipt.tracking = 'storage-unavailable'; report(); return; }
        reference.value = id;
        const returnParams = new URLSearchParams();
        if (params.get('view') === 'solar') returnParams.set('view', 'solar');
        if (['hybrid', 'solar-only'].includes(params.get('package'))) returnParams.set('package', params.get('package'));
        if (['commercial', 'property', 'trade'].includes(params.get('application'))) returnParams.set('application', params.get('application'));
        if (/^\d+$/.test(params.get('preview_theme_id') || '')) returnParams.set('preview_theme_id', params.get('preview_theme_id'));
        // Shopify needs its success flag when an explicit return_to replaces 'back'.
        // Bind measurement to this returned opaque reference, not to that flag alone.
        returnParams.set('contact_posted', 'true');
        returnParams.set('hok_solar_inquiry_ref', id);
        returnTo.value = `${window.location.pathname}?${returnParams.toString()}#${form.id}`;
        receipt.tracking = 'submission-pending';
        // An opaque reference is safe to inspect; it is also included in the native inquiry.
        receipt.reference = id;
        report();
      });
      return;
    }
    if (state !== 'success' || !valid(read(), form.id) || returnedReference !== read().id) {
      receipt.tracking = 'no-matching-pending-submission';
      report();
      return;
    }
    receipt.tracking = 'server-confirmed';
    receipt.reference = read().id;
    let attempts = 0;
    let timer;
    let privacyRequested = false;
    const send = () => {
      const pending = read();
      if (!valid(pending, form.id)) { receipt.tracking = 'pending-expired-or-unavailable'; report(); return; }
      const review = preview || pending.review !== false;
      const analyticsAllowed = permission('analyticsProcessingAllowed');
      const marketingAllowed = permission('marketingAllowed') && permission('saleOfDataAllowed');
      const tagReady = typeof window.gtag === 'function';
      receipt.ga = pending.ga ? 'already-queued' : !gaDestination ? 'disabled' : !analyticsAllowed ? 'consent-not-allowed' : !tagReady ? 'tag-unavailable' : 'eligible';
      receipt.ads = review ? 'review-suppressed' : pending.ads ? 'already-queued' : !adsDestination ? 'disabled' : !marketingAllowed ? 'consent-not-allowed' : !tagReady ? 'tag-unavailable' : 'eligible';
      const ga = receipt.ga === 'eligible';
      const ads = receipt.ads === 'eligible';
      if (ga || ads) {
        // Persist each destination before queueing: refresh/back and consent changes cannot repeat it.
        // Fail closed if storage cannot persist the consumed receipt; native inquiry delivery is unaffected.
        if (!write({ ...pending, ga: pending.ga || ga, ads: pending.ads || ads })) {
          receipt.tracking = 'confirmed-storage-unavailable'; report(); return;
        }
        const context = {
          page_location: window.location.origin + window.location.pathname,
          page_referrer: '', page_title: 'Containerized solar | House of Koval'
        };
        try {
          if (ga) {
            window.gtag('event', 'solar_pricing_request', {
              send_to: gaDestination, inquiry_id: pending.id, product_family: 'containerized_solar',
              package: pending.package, test_inquiry: review, debug_mode: review, ...context
            });
            receipt.ga = 'queued';
          }
          if (ads) {
            window.gtag('config', adsDestination.split('/')[0], { send_page_view: false });
            window.gtag('event', 'conversion', { send_to: adsDestination, transaction_id: pending.id, ...context });
            receipt.ads = 'queued';
          }
          receipt.tracking = 'events-queued';
        } catch {
          // At-most-once attempts are preferable to duplicate conversions. Provider receipt stays unverified.
          receipt.tracking = 'tag-call-failed-no-retry';
          report();
          return;
        }
      } else receipt.tracking = pending.ga || pending.ads ? 'already-recorded-or-partial' : 'confirmed-waiting';
      report();
      const awaiting = ['tag-unavailable', 'consent-not-allowed'].includes(receipt.ga) || ['tag-unavailable', 'consent-not-allowed'].includes(receipt.ads);
      if (awaiting && ++attempts < 30) {
        window.clearTimeout(timer);
        timer = window.setTimeout(send, 1000);
      }
      if (!window.Shopify?.customerPrivacy && !privacyRequested && typeof window.Shopify?.loadFeatures === 'function') {
        privacyRequested = true;
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => { if (!error) send(); });
      }
    };
    document.addEventListener('visitorConsentCollected', send);
    send();
  };
  document.querySelectorAll('[data-solar-inquiry-form]').forEach(initialize);
  document.addEventListener('shopify:section:load', event => {
    event.target.querySelectorAll('[data-solar-inquiry-form]').forEach(initialize);
  });
})();
